import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PoliticalPosition } from '../../../models/politicalPosition';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-political-position-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './political-position-list.component.html',
  styleUrl: './political-position-list.component.scss'
})
export class PoliticalPositionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  politicalPositions = signal<PoliticalPosition[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadPoliticalPositions();
  }

  loadPoliticalPositions(): void {
    this.spinnerService.show();
    this.lutService.getPoliticalPositions().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortPoliticalPositions(data);
        this.politicalPositions.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading political positions:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortPoliticalPositions(politicalPositions: PoliticalPosition[]): PoliticalPosition[] {
    return [...politicalPositions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreatePoliticalPosition(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(item: any): void {
    const politicalPosition = item as PoliticalPosition;
  }

  onEditPoliticalPosition(item: any): void {
    const politicalPosition = item as PoliticalPosition;
  }

  onDeletePoliticalPosition(item: any): void {
    const politicalPosition = item as PoliticalPosition;
    this.confirmationDialogService.openDeleteConfirmationDialog(politicalPosition.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalPoliticalPositions = this.politicalPositions();

          // 1. Optimistically remove from local state immediately
          this.politicalPositions.update(politicalPositions => politicalPositions.filter(pp => pp.id !== politicalPosition.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deletePoliticalPosition(politicalPosition.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Political position deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('politicalPositions', this.politicalPositions());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting political position:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.politicalPositions.set(originalPoliticalPositions);
              this.toast.error(`<strong>Failed to delete Political Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: any, newName: string} | {item: any, newValue: string, columnKey: string}): void {
    // Cast item to PoliticalPosition
    const item = event.item as PoliticalPosition;

    // Store original item for rollback in case of error
    const originalItem = { ...item };

    // Prepare updated item
    let updatedItem: PoliticalPosition;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.politicalPositions.update(politicalPositions => {
      const updated = politicalPositions.map(pp => pp.id === item.id ? updatedItem : pp);
      return this.sortPoliticalPositions(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updatePoliticalPosition(item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Political position updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('politicalPositions', this.politicalPositions());
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.politicalPositions.update(politicalPositions => {
          const updated = politicalPositions.map(pp => pp.id === item.id ? response : pp);
          return this.sortPoliticalPositions(updated);
        });
      },
      error: (error) => {
        console.error('Error updating political position:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.politicalPositions.update(politicalPositions =>
          politicalPositions.map(pp => pp.id === item.id ? originalItem : pp)
        );
        this.toast.error(`<strong>Failed to update Political Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newPoliticalPosition: PoliticalPosition = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.politicalPositions.update(politicalPositions => this.sortPoliticalPositions([...politicalPositions, newPoliticalPosition]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createPoliticalPosition({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Political position created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('politicalPositions', this.politicalPositions());
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.politicalPositions.update(politicalPositions => {
          const updated = politicalPositions.map(pp => pp.id === tempId ? response : pp);
          return this.sortPoliticalPositions(updated);
        });
      },
      error: (error) => {
        console.error('Error creating political position:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.politicalPositions.update(politicalPositions =>
          politicalPositions.filter(pp => pp.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Political Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
